import React from 'react';
import reactor from 'data/reactor';
import { Immutable } from 'nuclear-js';

import RuntimeGetters from 'data/app-runtime/getters';
import StudentGetters from 'data/student/getters';
import PartnerGetters from 'data/partner/getters';
import DataUtils from 'data/utils';
import StudentUtils from 'utils/students';
import { courses } from '@nwea/subject-course-data';
import CommonValues from 'resources/common-values';

import ModulesContainer from './normal-modules-container';
import ExpandedModulesContainer from './expanded-modules-container';

import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

export default CreateReactClass({
    displayName: 'student-modules-container',
    mixins: [reactor.ReactMixin],

    propTypes: {
        batchInfo: PropTypes.object /* Only exists when in Batch Print mode */,
        currentStudentBid: PropTypes.string.isRequired,
        currentTerm: PropTypes.instanceOf(Immutable.Map),
        expandedModule: PropTypes.string,
        isSingleStudentMode: PropTypes.bool,
        modalReferences: PropTypes.object,
        selectedTermNumber: PropTypes.number,
        studentInfo: PropTypes.instanceOf(Immutable.List).isRequired,
        studentTermData: PropTypes.instanceOf(Immutable.List),
        studentTransitionAction: PropTypes.string,
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        terms: PropTypes.instanceOf(Immutable.List),
        testEvents: PropTypes.instanceOf(Immutable.List),
    },

    getDataBindings() {
        return {
            keystoneTestEvent: StudentGetters.getKeystoneTestEvent,
            selectedGoals: RuntimeGetters.selectedGoals,
            growthGoals: StudentGetters.growthGoalsForCurrentStudentAndCourse,
            linkingStudiesData: StudentGetters.linkingStudiesForCurrentStudentAndSubject,
            futureTermsSetup: PartnerGetters.futureTermsSetup /* Only used once */,
            ritQuintiles: PartnerGetters.ritQuintiles,
            growthGoalNormsForStudent: StudentGetters.growthGoalNormsForCurrentStudent,
            growthProjections: StudentGetters.growthProjectionsForCurrentStudentAndSubject,
            /* I don't think we need these isLoaded getters because these components shouldn't load without these being true, but they shouldn't be replaced in this piece of work */
            ritQuintilesLoaded: StudentGetters.ritQuintilesLoadedForCurrentStudent,
            districtAveragesLoaded: StudentGetters.districtAveragesLoadedForCurrentStudent,
            districtAverages: PartnerGetters.districtAverages,
            growthProjectionsLoaded: StudentGetters.growthProjectionsLoadedForCurrentStudent,
            linkingStudiesLoaded: StudentGetters.linkingStudiesLoadedForCurrentStudentAndSubject,
            growthGoalNormsForStudentLoaded: StudentGetters.growthGoalNormsLoadedForCurrentStudent,
        };
    },

    getSelectedTermBySubject(subject) {
        let termInfo = new Map();
        let selectedTermNumber = this.props.selectedTermNumber
            ? this.props.selectedTermNumber
            : CommonValues.mostRecentTermNumber;
        let isMostRecent = selectedTermNumber === CommonValues.mostRecentTermNumber;
        const mostRecentTestEventForSubject = DataUtils.findMostRecentTestEventsBySubject(this.props.testEvents);
        if (isMostRecent && mostRecentTestEventForSubject && mostRecentTestEventForSubject.get(subject)) {
            selectedTermNumber = mostRecentTestEventForSubject.getIn([subject, 'termNumber']);
        }
        termInfo.set('selectedTermNumber', selectedTermNumber);
        termInfo.set('isMostRecent', isMostRecent);

        return termInfo;
    },

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.studentInfo !== null;
    },

    render() {
        const classes = this.props.studentTransitionAction
            ? 'student-modules-container former-student'
            : 'student-modules-container';
        const subjectBid = this.props.subject.get('bid');
        let subjectTestEvents = null;
        /**
         * Check if course specific before pulling test events to be used in Modules
         */
        if (this.state.keystoneTestEvent && this.state.keystoneTestEvent.get('isCourseSpecific') === true) {
            subjectTestEvents = DataUtils.testEventsForMsid(
                this.props.testEvents,
                this.state.keystoneTestEvent.get('measurementScaleID')
            );
        } else {
            subjectTestEvents = DataUtils.testEventsForSubject(this.props.testEvents, subjectBid);
        }
        const testEventsForSubject = subjectTestEvents ? subjectTestEvents : Immutable.List();
        const selectedTermInfo = this.getSelectedTermBySubject(subjectBid);

        const expandedOrNormal = this.props.expandedModule ? 'expanded' : 'normal';
        const sectionClasses = expandedOrNormal + ' ' + this.props.subject.get('shortName');

        if (expandedOrNormal === 'expanded') {
            return (
                <div className={classes}>
                    <section className={sectionClasses}>
                        <ExpandedModulesContainer
                            batchInfo={this.props.batchInfo}
                            currentTerm={this.props.currentTerm}
                            districtAverages={this.state.districtAverages}
                            districtAveragesLoaded={this.state.districtAveragesLoaded}
                            expandedModule={this.props.expandedModule}
                            growthGoalNormsForStudent={this.state.growthGoalNormsForStudent}
                            growthGoalNormsForStudentLoaded={this.state.growthGoalNormsForStudentLoaded}
                            growthGoals={this.state.growthGoals}
                            growthProjections={this.state.growthProjections}
                            growthProjectionsLoaded={this.state.growthProjectionsLoaded}
                            isMostRecentTerm={selectedTermInfo.get('isMostRecent')}
                            isSingleStudentMode={this.props.isSingleStudentMode}
                            key={`expanded-${this.props.subject.get('shortName')}`}
                            keystoneTestEvent={this.state.keystoneTestEvent}
                            linkingStudiesData={this.state.linkingStudiesData}
                            linkingStudiesLoaded={this.state.linkingStudiesLoaded}
                            modalReferences={this.props.modalReferences}
                            mode="expanded"
                            ritQuintiles={this.state.ritQuintiles}
                            ritQuintilesLoaded={this.state.ritQuintilesLoaded}
                            selectedGoals={this.state.selectedGoals}
                            selectedTermNumber={selectedTermInfo.get('selectedTermNumber')}
                            studentBid={this.props.currentStudentBid}
                            studentInfo={this.props.studentInfo.first()}
                            studentTermData={this.props.studentTermData}
                            subject={this.props.subject}
                            terms={this.props.terms}
                            testEvents={testEventsForSubject}
                        />
                    </section>
                </div>
            );
        } else {
            return (
                <div className={classes}>
                    <section className={sectionClasses}>
                        <ModulesContainer
                            batchInfo={this.props.batchInfo}
                            currentTerm={this.props.currentTerm}
                            selectedTermHasCourseSpecificTestEvent={
                                this.state.keystoneTestEvent
                                    ? this.state.keystoneTestEvent.get('isCourseSpecific')
                                    : false
                            }
                            courseName={
                                this.state.keystoneTestEvent ? this.state.keystoneTestEvent.get('courseName') : ''
                            }
                            key={`normal-${this.props.subject.get('shortName')}`}
                            keystoneTestEvent={this.state.keystoneTestEvent}
                            districtAverages={this.state.districtAverages}
                            districtAveragesLoaded={this.state.districtAveragesLoaded}
                            expandedModule={this.props.expandedModule}
                            futureTermsSetup={this.state.futureTermsSetup}
                            growthGoalNormsForStudent={this.state.growthGoalNormsForStudent}
                            growthGoalNormsForStudentLoaded={this.state.growthGoalNormsForStudentLoaded}
                            growthGoals={this.state.growthGoals}
                            growthProjections={this.state.growthProjections}
                            growthProjectionsLoaded={this.state.growthProjectionsLoaded}
                            isMostRecentTerm={selectedTermInfo.get('isMostRecent')}
                            isSingleStudentMode={this.props.isSingleStudentMode}
                            linkingStudiesData={this.state.linkingStudiesData}
                            linkingStudiesLoaded={this.state.linkingStudiesLoaded}
                            modalReferences={this.props.modalReferences}
                            mode="normal"
                            ritQuintiles={this.state.ritQuintiles}
                            ritQuintilesLoaded={this.state.ritQuintilesLoaded}
                            selectedGoals={this.state.selectedGoals}
                            selectedTermNumber={selectedTermInfo.get('selectedTermNumber')}
                            studentBid={this.props.currentStudentBid}
                            studentInfo={this.props.studentInfo.first()}
                            studentTermData={this.props.studentTermData}
                            subject={this.props.subject}
                            terms={this.props.terms}
                            testEvents={testEventsForSubject}
                        />
                    </section>
                </div>
            );
        }
    },
});
